<script lang="ts" setup>
import { computed, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import LogoIcon from "@/assets/images/logo.svg";
import { useSnackbarStore } from "@/store/snackbar.store";
import { useBaseView } from "@/composables/useBaseView";
import { useValidation } from "@/composables/useValidation";
import { API } from "@/api/api";
import { useCache } from "@/composables/useCache";
import { useAuthSession } from "@/composables/useAuthSession";
import { useCurrentDispatcher } from "@/composables/useCurrentDispatcher";
import { useChatStore } from "@/store/chat.store";
import { buildLoginQuery, readRedirectTarget } from "@/utils/authRedirect";
import { NOT_FOUND_ROUTE_NAME, REDIRECT_QUERY_PARAM, SIGN_OUT_REASON_QUERY_PARAM, START_PATH } from "@/constants/auth";
import { AuthSignOutReason } from "@/types/auth";

defineOptions({ name: "LoginPage" });

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const { clearCachedThreads } = useCache();
const { publishLogin } = useAuthSession();
const { loadCurrentDispatcher } = useCurrentDispatcher();
const chatStore = useChatStore();
const api = new API();

const snackbarStore = useSnackbarStore();

const { setTitle } = useBaseView();
const { requiredValidator } = useValidation();

const isFormValid = ref<boolean | null>(false);
const formError = ref("");
const email = ref("");
const password = ref("");
const showPass = ref(false);
const isLoading = ref(false);

const redirectTarget = computed(() => readRedirectTarget(route.query[REDIRECT_QUERY_PARAM]));

function resolveStartTarget(): string {
  const target = redirectTarget.value;

  if (!target) {
    return START_PATH;
  }

  const resolved = router.resolve(target);

  if (!resolved.matched.length || resolved.name === NOT_FOUND_ROUTE_NAME) {
    return START_PATH;
  }

  return target;
}

function reset() {
  formError.value = "";
  api.client.setToken("");
}

async function signIn() {
  reset();

  if (!isFormValid.value || isLoading.value) {
    return;
  }

  isLoading.value = true;

  try {
    const clearThreadsResp = await clearCachedThreads();
    if (!clearThreadsResp) {
      snackbarStore.showSnackbarError(t("auth.cache-clear-failed"), 5000);
      return;
    }

    chatStore.clearChat();

    const resp = await api.auth.login({ email: email.value, password: password.value });

    if (resp.success && resp.data?.token) {
      api.client.saveToken(resp.data.token);

      const dispatcher = await loadCurrentDispatcher();

      if (!dispatcher) {
        api.client.saveToken("");
        formError.value = "Server error";
        return;
      }

      publishLogin(dispatcher.id, dispatcher.company?.id ?? null);

      await router.push(resolveStartTarget());
      return;
    }

    if (resp.status === 400) {
      formError.value = "Wrong Email or password";
      return;
    }

    formError.value = `Server error (code: ${resp.error?.response?.status})`;
  } catch (e) {
    console.error("Error signing in", e);
    formError.value = "Server error";
  } finally {
    isLoading.value = false;
  }
}

onMounted(() => {
  setTitle("Welcome to Omni-dispatch TMS");

  const reason = route.query[SIGN_OUT_REASON_QUERY_PARAM];

  if (reason === AuthSignOutReason.SessionExpired) {
    snackbarStore.showSnackbarError(t("auth.session-expired"), 5000);
  }

  if (reason === AuthSignOutReason.SignedOut) {
    snackbarStore.showSnackbarError(t("auth.signed-out-in-another-tab"), 5000);
  }

  if (reason) {
    router.replace({ query: buildLoginQuery(redirectTarget.value) });
  }
});
</script>

<template>
  <v-layout class="login-form pa-0 align-center justify-center" full-height>
    <div class="pa-7 login-form__inner">
      <div class="login-form__top pt-10 pr-6 pb-8 pl-6">
        <div class="mb-8">
          <div class="login-form__logo mb-4">
            <logo-icon />
          </div>
          <div class="login-form__title text-h5 text-center font-weight-medium mb-2">Welcome to Omni-Dispatch TMS</div>
          <div class="login-form__subtitle text-grey-darken-1 text-body-2 text-center mb-0">Log in to your account</div>
        </div>

        <v-form v-model="isFormValid" @submit.prevent="signIn">
          <v-card variant="flat">
            <v-card-text class="px-0">
              <v-text-field
                v-model="email"
                :rules="[requiredValidator]"
                autocomplete="off"
                class="mb-5"
                color="primary"
                density="compact"
                hide-details="auto"
                label="E-mail"
                required
                type="email"
                variant="outlined"
              />
              <v-text-field
                v-model="password"
                :append-inner-icon="showPass ? 'mdi-eye-outline' : 'mdi-eye-off-outline'"
                :rules="[requiredValidator]"
                :type="showPass ? 'text' : 'password'"
                autocomplete="off"
                class="mb-4"
                color="primary"
                density="compact"
                hide-details="auto"
                label="Password"
                required
                variant="outlined"
                @click:append-inner="showPass = !showPass"
              />
              <v-slide-y-transition>
                <div v-if="formError" class="text-danger font-size-12">
                  {{ formError }}
                </div>
              </v-slide-y-transition>
            </v-card-text>
            <v-card-actions class="pa-0">
              <v-btn
                :loading="isLoading"
                class="mb-4 width-100 text-uppercase font-weight-600"
                color="primary"
                height="45"
                type="submit"
                variant="flat"
              >
                Log in
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-form>
        <div class="subtitle">
          <router-link
            :to="{ name: 'reset-password' }"
            class="pa-1 text-uppercase text-primary text-decoration-none font-weight-600 text-center d-block font-size-12"
          >
            Forgot password
          </router-link>
        </div>
      </div>

      <div class="login-form__bottom">
        <div class="d-flex align-center font-weight-medium">
          <span class="mr-4 text-grey-darken-3">Support:</span>
          <a class="text-primary" href="mailto:info@omni-dispatch.com">info@omni-dispatch.com</a>
        </div>
      </div>
    </div>
  </v-layout>
</template>

<style lang="scss" scoped>
.form {
  background-color: #f5f5ff;
}

.login-form__inner {
  position: relative;
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;

  .login-form__top {
    width: 430px;
    margin: 0 auto;
    box-shadow:
      0 3px 1px -2px rgba(0, 0, 0, 0.2),
      0px 2px 2px rgba(0, 0, 0, 0.14),
      0px 1px 5px rgba(0, 0, 0, 0.12);
    border-radius: 8px;
    background: #fff;
  }
}

.login-form__logo {
  max-width: 50px;
  margin: 0 auto;
}

.login-form__bottom {
  position: absolute;
  bottom: 35px;
}
</style>

<style lang="scss">
.login-form__inner {
  input:-webkit-autofill,
  input:-webkit-autofill:hover,
  input:-webkit-autofill:focus,
  input:-webkit-autofill:active {
    transition: background-color 5000s;
  }
}

.login-form__inner {
  .v-text-field--outlined.v-input--dense .v-label {
    top: 50%;
    transform: translateY(-50%);
  }

  .v-text-field--outlined.v-input--dense .v-label--active {
    top: 0;
    transform: translateY(-8px) scale(0.75);
  }

  .v-input__append-inner {
    align-self: center;
    margin-top: 0 !important;
  }
}
</style>
